<template>
  <v-row class="mb-4 suggestion" @mouseleave="onHover(false)" @mouseenter="onHover(true)" no-gutters>
    <v-hover v-slot="{ hover }">
      <v-col class="pa-2" :class="{ 'on-hover': hover }">
        <v-row justify="space-between" align="center" no-gutters>
          <v-col cols="auto" class="suggestion__date suggestion__text--light" :class="{ 'pb-2': !isSuggestionOwner }">
            {{ $dayjs(suggestion.added_at).format('YYYY-MM-DD HH:mm') }}
          </v-col>
          <v-col cols="auto">
            <v-tooltip v-if="isAdmin" color="font" top>
              <template v-slot:activator="{ on }">
                <v-btn v-on="on" color="success" text icon @click="openDialog('Accept', suggestion.id)">
                  <v-icon>mdi-check</v-icon>
                </v-btn>
              </template>
              <span> {{ $i18n.t('button.acceptChange') }} </span>
            </v-tooltip>
            <v-tooltip v-if="isSuggestionOwner" color="font" top>
              <template v-slot:activator="{ on }">
                <v-btn v-on="on" color="error" text icon @click="openDialog('Reject', suggestion.id)">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
              <span> {{ $i18n.t('button.rejectChange') }} </span>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-row align="center" class="suggestion__text" no-gutters>
          <v-col>
            <i18n path="sidebar.userSuggestedChange" tag="span">
              <template #user>
                <span class="font-italic">{{ suggestion.owner_name }}</span>
              </template>
            </i18n>
          </v-col>
        </v-row>
        <v-row align="center" class="suggestion__text" no-gutters>
          <v-col cols="auto" class="font-weight-bold">{{ suggestionType }}</v-col>
          <v-col cols="auto">
            <v-tooltip color="font" top>
              <template v-slot:activator="{ on }">
                <v-btn v-on="on" color="primary" text icon @click="zoomToFeature">
                  <v-icon>mdi-magnify</v-icon>
                </v-btn>
              </template>
              <span> {{ $i18n.t('button.zoomToSuggestion') }} </span>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-row
          v-if="!!suggestion.description"
          align="center"
          class="suggestion__text suggestion__text--light"
          no-gutters
        >
          <v-col class="font-italic">"{{ suggestion.description }}"</v-col>
        </v-row>
      </v-col>
    </v-hover>
    <main-dialog
      v-if="isDialogAcceptLoaded"
      :is-visible.sync="isDialogAcceptVisible"
      :title="$i18n.t('dialog.title.acceptChange')"
      :body="$i18n.t('dialog.body.acceptSuggestionConfirmation')"
      :loading="isDialogLoading"
      @agree="featureEditingHandler"
    />
    <main-dialog
      v-if="isDialogRejectLoaded"
      :is-visible.sync="isDialogRejectVisible"
      :title="$i18n.t('dialog.title.rejectChange')"
      :body="$i18n.t('dialog.body.rejectSuggestionConfirmation')"
      agree-text="dialog.discard"
      :loading="isDialogLoading"
      @agree="rejectChangeHandler"
    />
  </v-row>
</template>

<script>
import { call, get } from 'vuex-pathify';

export default {
  name: 'TheSuggestion',
  components: {
    MainDialog: () => import('@/components/MainDialog'),
  },
  data: () => ({
    isDialogAcceptLoaded: false,
    isDialogAcceptVisible: false,
    isDialogRejectLoaded: false,
    isDialogRejectVisible: false,
    isDialogLoading: false,
    suggestionId: null,
  }),
  props: {
    dataSourceName: {
      type: String,
      required: true,
    },
    featureId: {
      type: [String, Number],
      required: true,
    },
    layerId: {
      type: [Number, String],
      required: true,
    },
    suggestion: {
      type: Object,
      required: true,
    },
  },
  computed: {
    dataSources: get('layers/metadata'),
    currentUser: get('users/currentUser'),
    suggestionType() {
      return this.suggestion.suggestion_type === 'geom_new'
        ? this.$i18n.t('sidebar.newGeometry')
        : this.$i18n.t('sidebar.modificationGeometry');
    },
    isAdmin() {
      return this.currentUser.is_admin || this.currentUser.is_superadmin;
    },
    isSuggestionOwner() {
      return this.isAdmin || this.suggestion.owner_id === this.currentUser.id;
    },
    isTopologicalLayer() {
      const { scope } = this.dataSources[this.dataSourceName];
      return scope === 'water' || scope === 'sewer';
    },
  },
  methods: {
    deleteSuggestion: call('suggestions/deleteSuggestion'),
    editTopoFeatureAction: call('layers/editTopoDatasourceFeature'),
    editNonTopoFeatureAction: call('layers/editDatasourceFeature'),
    openDialog(dialogType, suggestionId) {
      this.suggestionId = suggestionId;
      this[`isDialog${dialogType}Loaded`] = true;
      this[`isDialog${dialogType}Visible`] = true;
    },
    featureEditingHandler() {
      if (this.isTopologicalLayer) {
        this.editTopoFeature();
      } else {
        this.editNonTopoFeature();
      }
    },
    async editTopoFeature() {
      const r = await this.acceptChangeHandler(this.editTopoFeatureAction);
      this.$root.$emit(
        'refreshMvtSources',
        r.data.data.map(layer => ({ id: layer, group: 'layers' })),
        true
      );
    },
    async editNonTopoFeature() {
      await this.acceptChangeHandler(this.editNonTopoFeatureAction);
      this.$root.$emit('refreshMvtSource', this.layerId);
    },
    async acceptChangeHandler(fn) {
      this.isDialogLoading = true;
      try {
        const payload = {
          datasource: this.dataSourceName,
          id: this.featureId,
          body: {
            geometry: this.suggestion.geometry,
            properties: {},
          },
          params: { layer_id: this.layerId },
        };
        const r = await fn(payload);
        this.$root.$emit('refreshTableData');
        this.$root.$emit('refreshFormData');
        await this.deleteSuggestion({
          data_source_name: this.dataSourceName,
          feature_id: this.featureId,
          suggestion_id: this.suggestionId,
        });
        return r;
      } finally {
        this.suggestionId = null;
        this.isDialogLoading = false;
        this.isDialogAcceptLoaded = false;
        this.isDialogAcceptVisible = false;
      }
    },
    async rejectChangeHandler() {
      this.isDialogLoading = true;
      try {
        await this.deleteSuggestion({
          data_source_name: this.dataSourceName,
          feature_id: this.featureId,
          suggestion_id: this.suggestionId,
        });
      } finally {
        this.suggestionId = null;
        this.isDialogLoading = false;
        this.isDialogRejectLoaded = false;
        this.isDialogRejectVisible = false;
      }
    },
    zoomToFeature() {
      const geometry = JSON.parse(JSON.stringify(this.suggestion.geometry));
      geometry.crs = { properties: { name: this.$_config.defaultEpsg }, type: 'name' };
      this.$root.$emit('createGeomAndFitView', geometry);
    },
    onHover(value) {
      this.$root.$emit('toggleFeatureHover', this.suggestion, value);
    },
  },
};
</script>

<style lang="scss" scoped>
.suggestion {
  @include border();
  .suggestion__date {
    font-size: 12px;
  }
  .suggestion__text {
    font-size: 14px;
    text-align: left;
    padding: 3px 0;
  }
  .suggestion__text--light {
    color: rgba(0, 0, 0, 0.59);
  }
  .on-hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
}
</style>
